/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * Link/button discovery for html_css_workspace sections.
 *
 * The Page Builder lets an admin point any link or button in pasted markup at a
 * page without editing the HTML. To offer that as a picker rather than a
 * selector box, the authored HTML is parsed here and every clickable element is
 * listed with a stable CSS selector. The selector — not the element itself — is
 * what gets stored in section_config.link_navigations and re-matched at render
 * time by arcee-client's htmlLinkNav.js.
 *
 * resolveNavHref mirrors the client helper of the same name; keep the two in
 * step or the preview stops matching what the published page does.
 */

const CLICKABLE_SELECTOR = 'a, button, [role="button"]';

/** CSS.escape where available (all evergreen browsers); conservative fallback for jsdom. */
function escapeIdent(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/([^\w-])/g, '\\$1');
}

/**
 * Builds a selector that identifies one element inside its document.
 *
 * Walks up to <body>, using :nth-of-type only where a tag has siblings of the
 * same type, and stops early at the first ancestor carrying an id — an id makes
 * everything above it irrelevant and survives edits elsewhere in the markup.
 */
export function buildSelector(el) {
  if (!el || el.nodeType !== 1) return '';

  const parts = [];
  let node = el;

  while (node && node.nodeType === 1 && node.tagName.toLowerCase() !== 'body') {
    if (node.id) {
      parts.unshift(`#${escapeIdent(node.id)}`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    const parent = node.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const current = node;
    const sameTag = Array.from(parent.children).filter((c) => c.tagName === current.tagName);
    parts.unshift(sameTag.length > 1 ? `${tag}:nth-of-type(${sameTag.indexOf(current) + 1})` : tag);
    node = parent;
  }

  return parts.join(' > ');
}

function labelFor(el) {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 60);
  const aria = el.getAttribute('aria-label') || el.getAttribute('title') || '';
  if (aria) return aria.slice(0, 60);
  const img = el.querySelector('img');
  if (img) return (img.getAttribute('alt') || 'Image link').slice(0, 60);
  return '';
}

/**
 * Lists every link/button in the authored HTML, in document order.
 * Duplicate selectors are dropped so a single row never maps twice.
 */
export function detectLinkTargets(html) {
  if (!html || !html.trim()) return [];

  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (_) {
    return [];
  }
  if (!doc?.body) return [];

  const seen = new Set();
  const targets = [];

  Array.from(doc.body.querySelectorAll(CLICKABLE_SELECTOR)).forEach((el) => {
    const selector = buildSelector(el);
    if (!selector || seen.has(selector)) return;
    seen.add(selector);
    targets.push({
      selector,
      tag: el.tagName.toLowerCase(),
      label: labelFor(el),
      href: el.getAttribute('href') || '',
    });
  });

  return targets;
}

/**
 * Joins detected elements with saved navigations.
 *
 * Rows come back in document order, each carrying its saved nav (if any).
 * Saved navs whose selector no longer matches anything — the markup was edited,
 * or the selector was typed by hand for JS-rendered markup — are appended with
 * `detected: false` so the admin can fix or remove them rather than lose them
 * silently.
 */
export function mergeLinkNavigations(html, navigations) {
  const saved = navigations || [];
  const detected = detectLinkTargets(html);
  const usedIds = new Set();

  const rows = detected.map((target) => {
    const nav = saved.find((n) => n.selector === target.selector);
    if (nav) usedIds.add(nav.id);
    return { ...target, nav: nav || null, detected: true };
  });

  saved.forEach((nav) => {
    if (usedIds.has(nav.id)) return;
    rows.push({
      selector: nav.selector,
      tag: nav.tag || 'a',
      label: nav.label || '',
      href: '',
      nav,
      detected: false,
    });
  });

  return rows;
}

/** Mirrors arcee-client's resolveNavHref — used for preview and row summaries. */
export function resolveNavHref(nav) {
  if (!nav) return null;

  if (nav.navType === 'page') {
    const slug = String(nav.targetPageSlug || '').trim().replace(/^\/+/, '');
    if (!slug) return null;
    const pairs = (nav.queryParams || [])
      .filter((p) => p && p.key)
      .map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value ?? '')}`);
    return pairs.length ? `/${slug}?${pairs.join('&')}` : `/${slug}`;
  }

  if (nav.navType === 'url') {
    const url = String(nav.url || '').trim();
    return url || null;
  }

  return null;
}

let _navSeq = 0;
export const newNavId = () => `lnav_${Date.now().toString(36)}_${(_navSeq++).toString(36)}`;

/**
 * Preview-only click bridge. The workspace preview must not actually navigate
 * the admin away from the builder, so configured elements report their
 * destination to the modal, which shows it as a toast instead.
 */
export function buildPreviewNavScript(navigations) {
  const entries = (navigations || [])
    .filter((nav) => nav && nav.selector)
    .map((nav) => ({ selector: nav.selector, href: resolveNavHref(nav) }))
    .filter((e) => e.href !== null);
  if (entries.length === 0) return '';

  const json = JSON.stringify(entries).replace(/</g, '\\u003c');

  return `;(function(){
  var NAVS = ${json};
  function bind() {
    for (var i = 0; i < NAVS.length; i++) {
      (function(nav){
        var nodes;
        try { nodes = document.querySelectorAll(nav.selector); } catch (err) { return; }
        for (var j = 0; j < nodes.length; j++) {
          (function(el){
            if (el.getAttribute('data-arcee-nav-preview') === '1') return;
            el.setAttribute('data-arcee-nav-preview', '1');
            el.style.cursor = 'pointer';
            el.addEventListener('click', function(e){
              e.preventDefault();
              e.stopPropagation();
              parent.postMessage({ type: 'ARCEE_NAV_PREVIEW', href: nav.href }, '*');
            });
          })(nodes[j]);
        }
      })(NAVS[i]);
    }
  }
  bind();
  if (document.readyState === 'complete') { setTimeout(bind, 0); } else { window.addEventListener('load', bind); }
  setTimeout(bind, 300);
})();`;
}
